import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { setHtmlId } from '@hallmark/web.util.set-html-id';
import { stringToKebabCase } from '@hallmark/web.util.string-to-kebab-case';
import { TabContent } from './fragments/tab-content';
import { TabItem } from './fragments/tab-item';
import { TextBlock } from './fragments/text-block';
import { keydownEventListener, keyupEventListener } from './helpers/helpers';
import { TabsTypesProps, TabObjectType, TabsBrandColors } from './tabs-types';
import styles from './tabs.module.scss';

export const Tabs: React.FC<TabsTypesProps> = ({
  addClass = '',
  anchorId = '',
  breakTabs,
  tabImageColor,
  tabsItems = [],
  backgroundColor = TabsBrandColors.White,
  bottomPadding,
  textBlock,
  headlineColor,
  headlineStyle2,
}: TabsTypesProps): React.ReactElement => {
  const sectionRef = useRef<HTMLElement>(null);
  const tablistRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const classes = classNames(styles.container, addClass);

  const wrapperClassNames = classNames(
    styles.tabs,
    backgroundColor && styles[`background-color-${String(backgroundColor).replace(' ', '-')}`],
    bottomPadding && styles[`tabs--padding-bottom-${String(bottomPadding).toLowerCase()}`],
    textBlock && styles['tabs--with-textBlock'],
  );

  const tabsArray: TabObjectType[] = tabsItems
    .map((tab) => {
      return {
        label: tab.tabLabel,
        content: tab.tabContent,
        buttonRef: tab.buttonRef,
        icon: tab.tabIcon,
      };
    })
    .filter((el) => el.content);

  return (
    <section
      {...setHtmlId(anchorId || 'tabs-anchor-id')}
      className={wrapperClassNames}
      ref={sectionRef}
      data-testid="tabs"
    >
      {textBlock && (
        <TextBlock
          {...textBlock}
          headlineStyle={headlineStyle2 || textBlock.headlineStyle}
          headlineColor={headlineColor || textBlock.headlineColor}
        />
      )}
      {breakTabs ? (
        tabsArray.map(({ label, content }, index) => <TabContent key={index} tabLabel={label} tabContent={content} />)
      ) : (
        <>
          <div className={classes}>
            <div className={styles.tablist} ref={tablistRef}>
              {tabsArray.map(
                ({ label, buttonRef, icon }, index) =>
                  label &&
                  icon &&
                  buttonRef && (
                    <TabItem
                      buttonRef={buttonRef}
                      id={`tab-${index}`}
                      key={`tab-${index}-${stringToKebabCase(label)}`}
                      svgCustomColor={tabImageColor || TabsBrandColors.Purple}
                      label={label}
                      selected={Boolean(selectedTab === index)}
                      index={index}
                      onClick={() => setSelectedTab(index)}
                      onKeyDown={(KeyboardEvent) => keydownEventListener(sectionRef, tabsArray, KeyboardEvent)}
                      onKeyUp={(KeyboardEvent) => keyupEventListener(tabsArray, KeyboardEvent)}
                      marginLarge={Boolean(tabsArray.length > 3)}
                      icon={icon}
                    />
                  ),
              )}
            </div>
            <div className={styles.tabContent}>
              <TabContent
                tabIndex={selectedTab || 0}
                tabLabel={tabsArray[Number(selectedTab)].label}
                tabContent={tabsArray[Number(selectedTab)].content}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};
